import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Button, DialogContent, makeStyles, MessageBar, MessageBarBody, Text, tokens, Tooltip } from '@fluentui/react-components'
import { bundleIcon, DeleteFilled, DeleteRegular, MailFilled, MailRegular } from '@fluentui/react-icons'
import { TaskMutationDto } from '../../../../config/taskConfig'
import { DocumentSortOrder } from '../../../../config/documentConfig'
import { UserListItemDto } from '../../../../config/userConfig'
import { FileListItemDto } from '../../../../context/FileContext'
import useRoles, { rolesWriteAllTask } from '../../../../hooks/useRoles'
import useTasks from '../../../../hooks/useTasks'
import { applyMinimumDuration, applyTaskDateShift, getTaskDateLabelKey, getVisibleTaskDates, TaskDateName } from '../../../../helpers/taskDateHelper'
import useTaskStatuses from '../../../../hooks/useTaskStatuses'
import useTaskAttachments from '../../../hooks/useTaskAttachments'
import { getTaskValidationSchema } from '../../../config/taskMutationConfig'
import useAxios from '../../../../hooks/useAxios'
import useUser from '../../../../hooks/useUser'
import useUsers from '../../../../hooks/useUsers'
import { outlookDisplayNewMessageForm } from '../../../../helpers/outlookHelper'
import { formatFullDate } from '../../../../helpers/dateHelper'
import { flatMapDriveItemsToOverviewItemDto } from '../../../../hooks/file/useFileDocuments'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikDurationField from '../../../../components/Formik/FormikDurationField'
import FormikField from '../../../../components/Formik/FormikField'
import LocalDatePicker, { LocalDatePickerProps } from '../../../../components/DatePicker/LocalDatePicker'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../../../components/Combobox/EnumMapCombobox'
import TaskStatusCombobox, { TaskStatusComboboxProps } from '../../../../components/Combobox/i18n/TaskStatusCombobox'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import FileCombobox from '../../../../components/Combobox/FileCombobox'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import DocumentFlatTree from '../../../../components/Document/DocumentFlatTree'
import DocumentLink from '../../../../components/Document/DocumentLink'
import Dropzone from '../../../../components/Dropzone/Dropzone'
import { TaskDialogBodyProps, TaskMutationDialogProps } from './TaskMutationDialogBody'
import FormContainer from '../../Form/FormContainer'
import FormikRichTextField from '../../../../components/Formik/FormikRichTextField'

const MailIcon = bundleIcon(MailFilled, MailRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)

const useStyles = makeStyles({
    attachmentList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
    // The link keeps its own margins; the remove button sits at the end of the row so all buttons line up.
    attachmentRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        '& > :first-child': { flexGrow: 1, minWidth: 0 },
    },
    quickActions: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
    // The document tree is as tall as the dossier is deep; keep it in its own scroll area so the dialog stays usable.
    tree: { maxHeight: '240px', overflowY: 'auto' },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    userRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        // Both fields carry Fluent's intrinsic min-width, which would make one track wider than the other.
        '& > *': { minWidth: 0 },
    },
    dateSeparator: { textAlign: 'center' },
})

/** Flattens Formik's nested errors to plain messages, mirroring Form.tsx's flatMapError. */
const flatMapError = (error: unknown): string[] => {
    if (!error || error instanceof Function) {
        return []
    }
    if (Array.isArray(error)) {
        return error.flatMap(flatMapError)
    }
    if (typeof error === 'object') {
        return Object.values(error).flatMap(flatMapError)
    }
    return [String(error)]
}

/**
 * The fields of the task add/edit dialog (HAM-4275). Everything is always visible; the content scrolls inside the
 * dialog while the title and the actions stay put.
 */
type Props = TaskMutationDialogProps & {
    /** Changes when the form was reset for another new task, so the cursor returns to the first field. */
    focusTrigger?: number
}

export default function TaskMutationDialogBodyContent(props: Props): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useStyles()
    const roles = useRoles()
    const { loaded: categoriesLoaded, categories: taskCategories, defaultCategory, dateConfig } = useTasks()
    const { taskStatuses, getStatusName } = useTaskStatuses()
    const axios = useAxios()
    const { currentUser } = useUser()
    const { getUserById } = useUsers()
    const { values, errors, dirty, setFieldValue, resetForm } = useFormikContext<TaskMutationDto>()

    const contentRef = useRef<HTMLDivElement>(null)
    // Set on the first keystroke; a field is focused automatically on open, so focus alone says nothing (HAM-4275).
    const userEdited = useRef<boolean>(false)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as TaskDialogBodyProps), [props])

    const taskCategoryOptions = useMemo<Map<string, string>>(
        () => new Map(taskCategories?.map(category => [category.key, category.name] as [string, string])),
        [categoriesLoaded, taskCategories],
    )

    const attachments = useTaskAttachments(values.fileId, values.attachmentIds, ids =>
        setFieldValue('attachmentIds', [...new Set([...(values.attachmentIds || []), ...ids])]),
    )

    /**
     * Set one date and let the other move along, so the due date never lies before the start date (HAM-4286). Both
     * fields are written every time; the helper returns the pair unchanged when nothing has to move.
     */
    const visibleDates = useMemo<TaskDateName[]>(() => getVisibleTaskDates(dateConfig), [dateConfig])

    const setDate = useCallback(
        (field: TaskDateName, date?: string) => {
            const dates = applyTaskDateShift({ start: values.start, due: values.due }, field, date)

            setFieldValue('start', dates.start)
            setFieldValue('due', dates.due)
        },
        [values.start, values.due, setFieldValue],
    )

    useEffect(() => {
        props.setValidationSchema?.(getTaskValidationSchema(translate, taskCategories?.length > 0, dateConfig))
    }, [categoriesLoaded, taskCategories, dateConfig])

    /**
     * A new task starts on the configured default category. The categories come from the settings and can arrive after
     * the dialog mounted, so this cannot be part of the initial values. It is applied with resetForm so it becomes the
     * new baseline: a setFieldValue would leave a freshly opened, untouched form dirty, and the unsaved-changes
     * warning would then fire on the very first Escape (HAM-4275).
     */
    useEffect(() => {
        if (!dialogBodyProps.id && !values.category && defaultCategory && !dirty && !userEdited.current) {
            resetForm({ values: { ...values, category: defaultCategory.key } })
        }
    }, [defaultCategory])

    /**
     * An existing task whose expected time is below the minimum the office set is lifted to that minimum while it is
     * being edited (HAM-4286, criterion 11). Like the default category this is a new baseline, not a change the user
     * made, so it goes through resetForm - and never after the first keystroke, which resetForm would swallow.
     */
    useEffect(() => {
        const minimum = applyMinimumDuration(values.duration, dateConfig.duration.minimum)

        if (dateConfig.duration.enabled && dialogBodyProps.id && minimum !== values.duration && !dirty && !userEdited.current) {
            resetForm({ values: { ...values, duration: minimum } })
        }
    }, [dateConfig.duration.minimum, dialogBodyProps.id, values.duration])

    // Clearing the dossier also clears its attachments; they live in that dossier's document tree.
    useEffect(() => {
        if (!values.fileId && values.attachmentIds?.length > 0) {
            setFieldValue('attachmentIds', [])
        }
    }, [values.fileId])

    useEffect(() => {
        if (props.focusTrigger) {
            // The field wrappers do not render a name attribute, so the first input in the content is the first field.
            contentRef.current?.querySelector<HTMLElement>('input, textarea')?.focus()
        }
    }, [props.focusTrigger])

    const errorMessages = useMemo<string[]>(() => flatMapError(errors), [errors])

    // Reveal the first invalid field when a submit fails; in a scrolling dialog it can sit below the fold.
    useEffect(() => {
        if (errorMessages.length === 0) {
            return undefined
        }
        const timer = setTimeout(
            () => contentRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
            0,
        )
        return () => clearTimeout(timer)
    }, [errors])
    return (
        <DialogContent onKeyDownCapture={() => (userEdited.current = true)}>
            <Dropzone disabled={!attachments.canUpload} tipKey={'task.attachments.upload'} onDropAccepted={files => attachments.uploadFiles(files)}>
                <FormContainer>
                    {props.unsavedWarning && (
                        <MessageBar intent={'warning'} layout={'multiline'}>
                            <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                        </MessageBar>
                    )}
                    {!values.fileId && (
                        <MessageBar intent={'info'} layout={'multiline'}>
                            <MessageBarBody>{translate('task.attachmentids.file.required')}</MessageBarBody>
                        </MessageBar>
                    )}
                    {attachments.attachmentFolderMissing && (
                        <MessageBar intent={'warning'} layout={'multiline'}>
                            <MessageBarBody>{translate('task.attachments.folder.missing')}</MessageBarBody>
                        </MessageBar>
                    )}

                    {dialogBodyProps.id && (
                        <div className={styles.quickActions}>
                            {taskStatuses.map(status => (
                                <Button
                                    key={status.alpha}
                                    disabled={values.status === status.alpha}
                                    onClick={() =>
                                        // Saves the task as it stands, including edits made in this dialog, and closes (AC16).
                                        axios.put(`/task/${dialogBodyProps.id}`, { ...values, status: status.alpha }).then(() => props.onDismiss?.())
                                    }
                                >
                                    {getStatusName(status.alpha)}
                                </Button>
                            ))}
                            <Tooltip content={translate('task.edit.email.action')} relationship={'description'} withArrow>
                                <Button
                                    icon={<MailIcon />}
                                    disabled={values.attachmentIds?.length > 0 && attachments.attachments === undefined}
                                    onClick={() =>
                                        outlookDisplayNewMessageForm({
                                            toRecipients: [values.assigneeId]
                                                .filter(userId => userId && userId !== currentUser?.id)
                                                .map(userId => getUserById(userId))
                                                .map(user => ({
                                                    emailAddress: user?.email?.trim(),
                                                    displayName: user?.fullName,
                                                })) as Office.EmailAddressDetails[],
                                            subject: `${values.title}${attachments.selectedFile ? ` [${attachments.selectedFile.reference}]` : ''}`,
                                            htmlBody: translate('task.edit.email.body', {
                                                // Built explicitly: the browser url now points at the screen behind the dialog.
                                                link: `${window.location.origin}${window.location.pathname}#${dialogBodyProps.baseUrl || '/task'}/${dialogBodyProps.id}/edit`,
                                                title: values.title?.replaceAll('<', '[').replaceAll('>', ']'),
                                                description: values.description,
                                                startDate: formatFullDate(values.start),
                                                dueDate: formatFullDate(values.due),
                                                assigneeName: getUserById(values.assigneeId)?.fullName,
                                                assignerName: getUserById(values.assignerId)?.fullName,
                                                file: attachments.selectedFile?.reference,
                                                workflowStep: values.workflowStepName,
                                                attachments: flatMapDriveItemsToOverviewItemDto(attachments.attachments || [])
                                                    ?.filter(item => (values.attachmentIds || []).includes(item.id))
                                                    .map(item => translate('task.edit.email.attachment', { name: item.name, link: item.url })),
                                                interpolation: { escapeValue: false },
                                            }),
                                        })
                                    }
                                >
                                    {translate('task.edit.email.action')}
                                </Button>
                            </Tooltip>
                        </div>
                    )}

                    {/* The file comes first: it decides which documents can be attached and which rates apply. */}
                    <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<FileListItemDto>>>
                        required
                        fieldName={'fileId'}
                        component={FileCombobox}
                    />
                    {taskCategories?.length > 0 && (
                        <FormikComboboxField<TaskMutationDto, EnumMapComboboxProps<string>>
                            required
                            fieldName={'category'}
                            component={EnumMapCombobox<string>}
                            componentProps={{ values: taskCategoryOptions, placeholderKey: 'task.category.label' }}
                        />
                    )}
                    <FormikInputField<TaskMutationDto> required fieldName={'title'} componentProps={{ placeholder: translate('task.title') }} />
                    <FormikRichTextField<TaskMutationDto>
                        allowLinks
                        fieldName={'description'}
                        placeholderKey={'task.description'}
                        componentProps={{ rows: 4 }}
                    />
                    {taskStatuses?.length > 0 && (
                        <FormikComboboxField<TaskMutationDto, Partial<TaskStatusComboboxProps>>
                            required
                            fieldName={'status'}
                            component={TaskStatusCombobox}
                        />
                    )}
                    {/* The office decides which dates a task has, which of them are required and how they are labelled
                        (HAM-4286). A date that is switched off is not rendered and not filled in either. The pickers
                        are not clamped onto each other: picking a date moves the other one along instead, so a task
                        can be planned further ahead without editing the other field first. */}
                    {dateConfig.twoDates ? (
                        <div className={styles.dateRow}>
                            {visibleDates.map((name, index) => (
                                <React.Fragment key={name}>
                                    {index > 0 && <Text className={styles.dateSeparator}>{translate('base.daterange.separator')}</Text>}
                                    <FormikField<LocalDatePickerProps, TaskMutationDto>
                                        required={dateConfig[name].required}
                                        fieldName={name}
                                        component={LocalDatePicker}
                                        componentProps={{
                                            isoValue: values[name],
                                            placeholderKey: getTaskDateLabelKey(name, dateConfig),
                                            onSelectDate: date => setDate(name, date),
                                        }}
                                    />
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        visibleDates.map(name => (
                            <FormikField<LocalDatePickerProps, TaskMutationDto>
                                key={name}
                                required={dateConfig[name].required}
                                fieldName={name}
                                component={LocalDatePicker}
                                componentProps={{
                                    isoValue: values[name],
                                    placeholderKey: getTaskDateLabelKey(name, dateConfig),
                                    onSelectDate: date => setDate(name, date),
                                }}
                            />
                        ))
                    )}
                    {dateConfig.duration.enabled && (
                        <FormikDurationField<TaskMutationDto> fieldName={'duration'} componentProps={{ placeholder: translate('task.duration') }} />
                    )}
                    <div className={styles.userRow}>
                        <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<UserListItemDto>>>
                            required
                            fieldName={'assigneeId'}
                            component={UserCombobox}
                            labelKey={'task.assigneeid'}
                            componentProps={{ placeholderKey: 'task.assigneeid' }}
                        />
                        <FormikComboboxField<TaskMutationDto, Partial<SubComboboxProps<UserListItemDto>>>
                            required
                            fieldName={'assignerId'}
                            component={UserCombobox}
                            labelKey={'task.assignerid'}
                            componentProps={{
                                readOnly: !roles.any(rolesWriteAllTask),
                                placeholderKey: 'task.assignerid',
                            }}
                        />
                    </div>
                    {values.workflowStepName && (
                        <FormikInputField<TaskMutationDto>
                            fieldName={'workflowStepName'}
                            componentProps={{ readOnly: true, placeholder: translate('task.workflowstep') }}
                        />
                    )}
                    {values.fileId && attachments.attachments === undefined && <Text>{translate('task.attachmentids.loading')}</Text>}
                    {attachments.attachments !== undefined && !(values.attachmentIds?.length > 0) && (
                        <Text>{translate('task.attachmentids.empty')}</Text>
                    )}
                    {attachments.attachments !== undefined && values.attachmentIds?.length > 0 && (
                        <div className={styles.attachmentList}>
                            {values.attachmentIds.map(attachmentId => (
                                <div key={attachmentId} className={styles.attachmentRow}>
                                    <DocumentLink
                                        id={attachmentId}
                                        document={attachments.attachments.find(attachment => attachment.id === attachmentId)}
                                    />
                                    {/* Deselecting from the list saves hunting the document down in the tree again. */}
                                    <Tooltip content={translate('task.attachmentids.remove')} relationship={'label'} withArrow>
                                        <Button
                                            appearance={'subtle'}
                                            icon={<DeleteIcon />}
                                            aria-label={translate('task.attachmentids.remove')}
                                            onClick={() =>
                                                setFieldValue(
                                                    'attachmentIds',
                                                    values.attachmentIds.filter(id => id !== attachmentId),
                                                )
                                            }
                                        />
                                    </Tooltip>
                                </div>
                            ))}
                        </div>
                    )}
                    {attachments.selectedFile && (
                        <div className={styles.tree}>
                            <DocumentFlatTree
                                rootId={attachments.selectedFile.documentRootId}
                                allowFolderSelection={false}
                                selectedOptions={values.attachmentIds}
                                onOptionsSelect={(selected, items) => {
                                    // The tree hands over the items it already has, so linking one costs no request.
                                    attachments.registerKnownItems(items)
                                    setFieldValue('attachmentIds', selected)
                                }}
                                sortOrder={DocumentSortOrder.NameAsc}
                                // Start from the tree the dossier screen already loaded when there is one (HAM-4295).
                                treeCache={'read-write'}
                            />
                        </div>
                    )}
                    {/*Source - https://stackoverflow.com/a/477699*/}
                    <input type="submit" hidden />
                </FormContainer>
            </Dropzone>
        </DialogContent>
    )
}
